/**
 * Offline-first upload queue for ALL platforms (web + native).
 *
 * Flow:
 *   1. Researcher selects audio files
 *   2. Files are instantly saved to local storage (IndexedDB on web, Capacitor Filesystem on native)
 *   3. Queue entry is persisted in IndexedDB
 *   4. Researcher sees "Saved!" immediately
 *   5. Queue processor uploads to cloud when online
 *   6. Local files cleaned up after successful upload
 *
 * State machine per queue item:
 *   QUEUED → UPLOADING → COMPLETED (cleanup local file)
 *                      → FAILED (retry up to 5x)
 *          → PAUSED (offline, resumes on connectivity)
 *
 * Survives: page refresh, app kill, phone restart, network loss.
 */

// ── Platform detection ────────────────────────────────────────────────

function _isNative() {
  try { return window.Capacitor?.isNativePlatform?.() === true; } catch { return false; }
}

// ── IndexedDB helpers (queue metadata) ────────────────────────────────

const DB_NAME = 'researcher-upload-queue';
const DB_VERSION = 1;
const STORE_NAME = 'queue';

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function dbPut(item) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(item);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function dbGet(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(id);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

async function dbDelete(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function dbGetAll() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

// ── IndexedDB file blob store (web only) ──────────────────────────────

const FILE_DB_NAME = 'researcher-upload-files';
const FILE_STORE = 'files';

function openFileDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(FILE_DB_NAME, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(FILE_STORE)) {
        req.result.createObjectStore(FILE_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ── File operations (platform-adaptive) ───────────────────────────────

const QUEUE_DIR = 'upload-queue';

async function copyFileToAppStorage(file, queueId, fileIndex) {
  const key = `${queueId}_${fileIndex}`;

  // ALWAYS use IndexedDB for file storage — on both web AND native.
  // Store as Blob (not ArrayBuffer) so IndexedDB can handle it without
  // loading the entire file into JS heap memory. A 140MB file.arrayBuffer()
  // allocates 140MB on the JS heap, which OOMs on Android WebViews.
  // Blobs are stored by reference — IndexedDB writes them to disk incrementally.
  const blob = new Blob([file], { type: file.type || 'application/octet-stream' });
  const db = await openFileDB();
  await new Promise((resolve, reject) => {
    const tx = db.transaction(FILE_STORE, 'readwrite');
    tx.objectStore(FILE_STORE).put(blob, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  return `idb://${key}`;
}

async function readFileFromAppStorage(filePath) {
  if (filePath.startsWith('idb://')) {
    // IndexedDB: used by both web AND native (since we now store all files here)
    const key = filePath.slice(6);
    const db = await openFileDB();
    const stored = await new Promise((resolve, reject) => {
      const tx = db.transaction(FILE_STORE, 'readonly');
      const req = tx.objectStore(FILE_STORE).get(key);
      req.onsuccess = () => {
        if (!req.result) reject(new Error('File not found in local storage'));
        else resolve(req.result);
      };
      req.onerror = () => reject(req.error);
    });
    // Stored data may be a Blob (new path) or ArrayBuffer (legacy).
    // Return as-is — the upload code handles both via the queue processor.
    return stored;
  }

  // Legacy: Capacitor Filesystem path (for native recorder chunks that have a filesystem path)
  if (_isNative()) {
    // Try reading via Capacitor.convertFileSrc + fetch (no bridge for file data)
    try {
      const { nativeGetFileInfo, isNativeUploadAvailable } = await import('../native/nativeUpload');
      if (isNativeUploadAvailable()) {
        const info = await nativeGetFileInfo(filePath);
        if (!info.exists) throw new Error('File not found in local storage');
        const webPath = window.Capacitor?.convertFileSrc
          ? window.Capacitor.convertFileSrc(`file://${info.absolutePath}`)
          : `file://${info.absolutePath}`;
        const response = await fetch(webPath);
        if (!response.ok) throw new Error(`Failed to read file: ${response.status}`);
        return await response.arrayBuffer();
      }
    } catch (e) {
      console.warn('[queue] Native file read via fetch failed, falling back to Capacitor:', e.message);
    }
  }

  // Last resort: Capacitor Filesystem (only for small native recorder chunks)
  try {
    const mod = await import(/* webpackIgnore: true */ '@capacitor/filesystem');
    const result = await mod.Filesystem.readFile({ path: filePath, directory: mod.Directory.Data });
    const binary = atob(result.data);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  } catch (e) {
    throw new Error('Cannot read file: ' + filePath);
  }
}

async function deleteLocalFile(filePath) {
  try {
    if (filePath.startsWith('idb://')) {
      // IndexedDB: used by both web AND native
      const key = filePath.slice(6);
      const db = await openFileDB();
      await new Promise((resolve, reject) => {
        const tx = db.transaction(FILE_STORE, 'readwrite');
        tx.objectStore(FILE_STORE).delete(key);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } else {
      // Legacy: Capacitor Filesystem path (native recorder chunks)
      try {
        const mod = await import(/* webpackIgnore: true */ '@capacitor/filesystem');
        await mod.Filesystem.deleteFile({ path: filePath, directory: mod.Directory.Data });
      } catch (e) {
        console.warn('Native filesystem not available:', e.message);
      }
    }
  } catch (e) {
    console.warn('deleteLocalFile warning:', e.message);
  }
}

// ── Network helpers (platform-adaptive) ───────────────────────────────

async function getNetworkStatus() {
  if (_isNative()) {
    try {
      const mod = await import(/* webpackIgnore: true */ '@capacitor/network');
      return mod.Network.getStatus();
    } catch { return { connected: navigator.onLine }; }
  }
  return { connected: navigator.onLine };
}

// ── Queue operations ─────────────────────────────────────────────────

const MAX_QUEUE_SIZE = 5;
const MAX_RETRIES = 5;

/**
 * Add files to the upload queue.
 * Copies files to local storage, persists queue entry, returns immediately.
 */
// Serialization lock to prevent race condition when user taps upload twice rapidly
let _enqueueLock = null;

export async function enqueueUpload(files, date, sessionType) {
  while (_enqueueLock) await _enqueueLock;
  let unlock;
  _enqueueLock = new Promise(r => { unlock = r; });
  try {
    return await _enqueueUploadInner(files, date, sessionType);
  } finally {
    _enqueueLock = null;
    unlock();
  }
}

async function _enqueueUploadInner(files, date, sessionType) {
  const existing = await dbGetAll();
  const activeItems = existing.filter(i => i.status !== 'completed');
  if (activeItems.length >= MAX_QUEUE_SIZE) {
    throw new Error(`Queue full (${MAX_QUEUE_SIZE} max). Wait for current uploads to finish.`);
  }

  // Duplicate detection: same file name + size + date
  for (const item of activeItems) {
    for (const f of files) {
      const isDup = item.files.some(qf => qf.originalName === f.name && qf.size === f.size) && item.date === date;
      if (isDup) {
        throw new Error(`"${f.name}" is already queued for ${date}.`);
      }
    }
  }

  const queueId = `q_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  const fileEntries = [];

  for (let i = 0; i < files.length; i++) {
    const f = files[i];

    // Native recorder chunks are already on the filesystem — skip copying
    if (f && f.nativePath) {
      fileEntries.push({
        localPath: f.nativePath,
        originalName: f.name,
        type: f.type || 'audio/mp4',
        size: f.size,
      });
      continue;
    }

    let localPath;
    try {
      localPath = await copyFileToAppStorage(f, queueId, i);
    } catch (storageErr) {
      // IndexedDB quota exceeded — clean up stale items and retry once
      if (storageErr.name === 'QuotaExceededError' || storageErr.message?.includes('quota')) {
        console.warn('[queue] Storage quota exceeded, cleaning up stale items and retrying');
        await cleanupCompleted();
        // Also force-clean any permanently failed items regardless of age
        const stale = await dbGetAll();
        for (const item of stale) {
          if (item.status === 'failed' && item.retryCount >= MAX_RETRIES) {
            await removeFromQueue(item.id);
          }
        }
        localPath = await copyFileToAppStorage(f, queueId, i);
      } else {
        throw storageErr;
      }
    }
    fileEntries.push({
      localPath,
      originalName: f.name,
      type: f.type || 'audio/mpeg',
      size: f.size,
    });
  }

  const entry = {
    id: queueId,
    files: fileEntries,
    date,
    sessionType: sessionType || 'evening_findings',
    status: 'queued', // queued | uploading | completed | failed
    retryCount: 0,
    lastError: null,
    progress: 0,
    sessionId: null,
    createdAt: new Date().toISOString(),
  };

  await dbPut(entry);
  // Request background sync so uploads survive phone backgrounding
  requestBackgroundSync();
  return entry;
}

/**
 * Get all queue items, sorted by creation time (newest first).
 */
export async function getQueueItems() {
  const items = await dbGetAll();
  return items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
}

/**
 * Remove a queue item and clean up its local files.
 */
export async function removeFromQueue(id) {
  const item = await dbGet(id);
  if (item) {
    for (const f of item.files) {
      await deleteLocalFile(f.localPath);
    }
    await dbDelete(id);
  }
}

/**
 * Update a queue item's fields.
 */
export async function updateQueueItem(id, updates) {
  const item = await dbGet(id);
  if (!item) return null;
  const updated = { ...item, ...updates };
  await dbPut(updated);
  return updated;
}

/**
 * Clean up completed items older than 1 hour AND permanently failed items older than 24 hours.
 * This prevents IndexedDB from filling up with stale file blobs (50-65MB each),
 * which blocks new uploads on Android phones with limited storage quotas.
 */
export async function cleanupCompleted() {
  const items = await dbGetAll();
  const completedCutoff = Date.now() - 60 * 60 * 1000;       // 1 hour for completed
  const failedCutoff = Date.now() - 24 * 60 * 60 * 1000;     // 24 hours for permanently failed
  for (const item of items) {
    const age = new Date(item.createdAt).getTime();
    if (item.status === 'completed' && age < completedCutoff) {
      await removeFromQueue(item.id);
    } else if (item.status === 'failed' && item.retryCount >= MAX_RETRIES && age < failedCutoff) {
      console.log(`[queue] Cleaning up permanently failed item ${item.id} (${item.files.map(f => f.originalName).join(', ')})`);
      await removeFromQueue(item.id);
    }
  }
}

/**
 * Remove all permanently failed items and their file blobs.
 * Called when user taps "Clear failed" or when storage is low.
 */
export async function clearAllFailed() {
  const items = await dbGetAll();
  let cleared = 0;
  for (const item of items) {
    if (item.status === 'failed') {
      await removeFromQueue(item.id);
      cleared++;
    }
  }
  return cleared;
}

// ── Queue processor ──────────────────────────────────────────────────

let isProcessing = false;
let processingStartedAt = 0;
let onQueueChangeCallback = null;

// Stored upload function — set once by UploadContext so all queue
// triggers (network listener, manual retry) use the same implementation.
let _storedUploadFn = null;

export function setQueueUploadFn(fn) {
  _storedUploadFn = fn;
}

export function onQueueChange(cb) {
  onQueueChangeCallback = cb;
}

function notifyChange() {
  if (onQueueChangeCallback) onQueueChangeCallback();
}

/**
 * Process all queued items. Called on app open and when connectivity returns.
 * Only one processor runs at a time (isProcessing guard).
 */
export async function processQueue(uploadFn) {
  // Fall back to stored function if none passed explicitly
  if (!uploadFn) uploadFn = _storedUploadFn;
  if (!uploadFn) {
    console.warn('[queue] processQueue called but no upload function registered — skipping');
    return;
  }
  // Stale processing detection: if stuck for > 10 minutes, reset the lock.
  // This happens when JS was killed mid-upload (e.g. phone backgrounded)
  // and the isProcessing flag never got reset.
  if (isProcessing && Date.now() - processingStartedAt > 3 * 60 * 1000) {
    console.warn('[queue] Processing appears stuck (>3min), resetting lock');
    isProcessing = false;
  }

  if (isProcessing) return;
  isProcessing = true;
  processingStartedAt = Date.now();

  try {
    // Reset stale 'uploading' queue items — these are from a previous processQueue
    // call that was killed (e.g. phone backgrounded). Since we hold the isProcessing
    // lock, any item still in 'uploading' is orphaned and safe to retry.
    const staleCheck = await dbGetAll();
    for (const item of staleCheck) {
      if (item.status === 'uploading') {
        console.warn(`[queue] Resetting orphaned uploading item ${item.id} (${item.files.map(f => f.originalName).join(', ')})`);
        await updateQueueItem(item.id, { status: 'queued', progress: 0 });
      }
    }

    const status = await getNetworkStatus();
    if (!status.connected) {
      console.log('[queue] Offline — skipping queue processing');
      return;
    }

    const items = await dbGetAll();
    const nowMs = Date.now();
    const pending = items
      .filter(i => {
        if (i.status === 'queued') return true;
        if (i.status === 'failed' && i.retryCount < MAX_RETRIES) {
          // Exponential backoff: skip items whose retryAfter hasn't elapsed
          if (i.retryAfter && i.retryAfter > nowMs) return false;
          return true;
        }
        return false;
      })
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)); // oldest first

    for (const item of pending) {
      // Re-check connectivity before each item
      const netStatus = await getNetworkStatus();
      if (!netStatus.connected) {
        console.log('[queue] Lost connectivity, pausing queue');
        break;
      }

      await updateQueueItem(item.id, { status: 'uploading', progress: 0 });
      notifyChange();

      try {
        // Read files from local storage (returns Blob or ArrayBuffer)
        const fileBuffers = [];
        for (const f of item.files) {
          const stored = await readFileFromAppStorage(f.localPath);
          // Normalize to Blob — avoids loading entire file into JS heap as ArrayBuffer
          const buffer = (stored instanceof Blob)
            ? stored
            : new Blob([stored], { type: f.type || 'application/octet-stream' });
          fileBuffers.push({
            buffer,
            name: f.originalName,
            type: f.type,
            size: f.size,
          });
        }
        // Attach native file paths & queue ID for native upload path
        fileBuffers._nativePaths = item.files.map(f => f.localPath);
        fileBuffers._queueId = item.id;

        // State manager for resumable uploads — lets the upload function save/read
        // progress to IndexedDB so it can resume after interruption
        const stateManager = {
          getState: () => item.uploadState || null,
          saveState: async (state) => {
            item.uploadState = state;
            await updateQueueItem(item.id, { uploadState: state });
          },
        };

        // Use the provided upload function (from UploadContext)
        const sessionId = await uploadFn(fileBuffers, item.date, (progress) => {
          // Progress callback — update queue item
          updateQueueItem(item.id, { progress }).then(notifyChange);
        }, stateManager, item.sessionType);

        // Success — clean up local files and upload state
        for (const f of item.files) {
          await deleteLocalFile(f.localPath);
        }
        await updateQueueItem(item.id, { status: 'completed', progress: 100, sessionId, uploadState: null });
        notifyChange();
        console.log(`[queue] Item ${item.id} uploaded successfully → session ${sessionId}`);
      } catch (err) {
        const httpStatus = err.response?.status;
        // 429 (rate limit) and 4xx validation errors won't succeed on retry — fail permanently.
        const permanent = httpStatus === 429 || (httpStatus >= 400 && httpStatus < 500 && httpStatus !== 401 && httpStatus !== 408);
        const retryCount = item.retryCount + 1;
        const status = permanent || retryCount >= MAX_RETRIES ? 'failed' : 'queued';
        // Exponential backoff: 30s, 60s, 2min, 4min, 8min
        const retryAfter = permanent ? null : Date.now() + (30000 * Math.pow(2, retryCount - 1));
        await updateQueueItem(item.id, {
          status,
          retryCount: permanent ? MAX_RETRIES : retryCount,
          retryAfter,
          lastError: err.message?.slice(0, 500) || 'Unknown error',
          lastErrorStatus: httpStatus || null,
          progress: 0,
        });
        notifyChange();
        if (permanent) {
          console.error(`[queue] Item ${item.id} permanently failed (HTTP ${httpStatus}): ${err.message}`);
        } else {
          console.error(`[queue] Item ${item.id} failed (attempt ${retryCount}/${MAX_RETRIES}), next retry after ${Math.round((retryAfter - Date.now()) / 1000)}s:`, err.message);
        }

        // If auth error, stop processing queue
        if (httpStatus === 401) {
          console.log('[queue] Auth expired, stopping queue processing');
          break;
        }
      }
    }
  } finally {
    isProcessing = false;
  }
}

/**
 * Set up network listener to auto-process queue when connectivity returns.
 */
let networkListenerRegistered = false;

export function setupNetworkListener(uploadFn) {
  // Store the function so subsequent processQueue() calls use it
  if (uploadFn) _storedUploadFn = uploadFn;

  if (networkListenerRegistered) return;
  networkListenerRegistered = true;

  if (_isNative()) {
    import(/* webpackIgnore: true */ '@capacitor/network').then(({ Network }) => {
      Network.addListener('networkStatusChange', (status) => {
        console.log(`[queue] Network changed: connected=${status.connected}, type=${status.connectionType}`);
        if (status.connected) {
          setTimeout(() => processQueue(), 2000); // uses _storedUploadFn
        }
      });
    });
  } else {
    window.addEventListener('online', () => {
      console.log('[queue] Browser came online, processing queue');
      setTimeout(() => processQueue(), 2000); // uses _storedUploadFn
    });
  }
}

/**
 * Get count of pending items (for badge display).
 */
export async function getPendingCount() {
  const items = await dbGetAll();
  return items.filter(i => i.status === 'queued' || i.status === 'uploading').length;
}

/**
 * Request Background Sync so the browser retries uploads even if the tab is backgrounded.
 * Falls back silently if not supported.
 */
export async function requestBackgroundSync() {
  try {
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      const reg = await navigator.serviceWorker.ready;
      await reg.sync.register('upload-queue');
    }
  } catch (e) {
    // Background sync not available — uploads will retry on next app open
  }
}
